import { Injectable } from '@angular/core';
import { DomainUsersEntity, DomainGroupMembersEntity, UnsavedUser } from '@monsido/modules/models/api/interfaces/user.interface';
import { UserImportCSVData, CSVFormat } from '../steps/setup/setup.component';
import { ErrorUtilService } from '../../../../ng2/modules/endpoints/service/error-util.service';

/**
 * Service for handling CSV-related operations in user import functionality.
 * Centralizes CSV parsing, format detection, and utility functions to eliminate code duplication.
 */
@Injectable({
    providedIn: 'root',
})
export class UserImportCsvService {

    constructor (private errorUtilService: ErrorUtilService) { }

    /**
     * Parse comma-separated IDs from a CSV cell value
     * Handles comma-separated values and quoted strings
     * @param cellValue The raw cell value from CSV
     * @returns Array of IDs as numbers
     */
    parseIds (cellValue: string): number[] {
        if (!cellValue || typeof cellValue !== 'string') {
            return [];
        }

        // Remove quotes and trim whitespace
        const cleanValue = cellValue.replace(/^["']|["']$/g, '').trim();

        if (!cleanValue) {
            return [];
        }

        // Split by comma and parse as numbers
        return cleanValue
            .split(',')
            .map(id => id.trim())
            .filter(id => id.length > 0)
            .map(id => parseInt(id, 10))
            .filter(id => !isNaN(id) && id > 0);
    }

    /**
     * Detect the CSV format based on column headers
     * @param data The CSV data to analyze
     * @returns The detected CSV format ('basic' or 'enhanced')
     */
    detectCSVFormat (data: UserImportCSVData): CSVFormat {
        if (!data.data || data.data.length === 0) {
            return 'basic';
        }

        const headers = data.data[0].map((h: string) => h.toLowerCase().trim());

        // Look for ID-based domain columns
        const domainIdPatterns = ['domain id', 'domain ids', 'domainid', 'domainids', 'domain_id', 'domain_ids'];
        const domainGroupIdPatterns = ['domain group id', 'domain group ids', 'domaingroupid', 'domaingroupids', 'domain_group_id', 'domain_group_ids'];

        const hasDomainIdsColumn = headers.some((h: string) =>
            domainIdPatterns.some(pattern => h.includes(pattern)),
        );
        const hasDomainGroupIdsColumn = headers.some((h: string) =>
            domainGroupIdPatterns.some(pattern => h.includes(pattern)),
        );

        return (hasDomainIdsColumn || hasDomainGroupIdsColumn) ? 'enhanced' : 'basic';
    }

    /**
     * Find the column index for domain IDs in the CSV headers
     * @param headers The CSV headers array
     * @returns Column index or -1 if not found
     */
    findDomainIdsColumnIndex (headers: string[]): number {
        const normalizedHeaders = headers.map((h: string) => h.toLowerCase().trim());
        const domainIdPatterns = ['domain id', 'domain ids', 'domainid', 'domainids', 'domain_id', 'domain_ids'];

        return normalizedHeaders.findIndex((header: string) =>
            domainIdPatterns.some(pattern => header.includes(pattern)),
        );
    }

    /**
     * Find the column index for domain group IDs in the CSV headers
     * @param headers The CSV headers array
     * @returns Column index or -1 if not found
     */
    findDomainGroupIdsColumnIndex (headers: string[]): number {
        const normalizedHeaders = headers.map((h: string) => h.toLowerCase().trim());
        const domainGroupIdPatterns = ['domain group id', 'domain group ids', 'domaingroupid', 'domaingroupids', 'domain_group_id', 'domain_group_ids'];

        return normalizedHeaders.findIndex((header: string) =>
            domainGroupIdPatterns.some(pattern => header.includes(pattern)),
        );
    }

    /**
     * Extract error message from API response
     * Handles different error response formats consistently
     * @param error The error object from API response
     * @returns Formatted error message string
     */
    extractErrorMessage (error: unknown): string {
        return this.errorUtilService.extractTextErrorMessage(error);
    }

    /**
     * Create a domain user assignment object
     * @param domainId The domain ID
     * @param user The user object
     * @returns Domain user assignment object
     */
    createDomainUserAssignment (domainId: number, user: UnsavedUser): DomainUsersEntity {
        return {
            domain_id: domainId,
            visible: user.customer_admin || true, // Default to visible for CSV-specified domains
            send_report: true, // Default to send reports for CSV-specified domains
        };
    }

    /**
     * Create a domain group member assignment object
     * @param domainGroupId The domain group ID
     * @param user The user object
     * @returns Domain group member assignment object
     */
    createDomainGroupAssignment (domainGroupId: number, user: UnsavedUser): DomainGroupMembersEntity {
        return {
            domain_group_id: domainGroupId,
            visible: user.customer_admin || true, // Default to visible for CSV-specified domains
            send_report: true, // Default to send reports for CSV-specified domains
        };
    }

    /**
     * Calculate visibility for domain assignment based on user admin status and domain settings
     * @param user The user object
     * @param originalVisible The original visibility setting from domain/selection
     * @returns Calculated visibility value
     */
    calculateVisibility (user: UnsavedUser, originalVisible: boolean): boolean {
        return user.customer_admin || originalVisible;
    }

    /**
     * Calculate send_report for domain assignment based on visibility and original settings
     * @param visible The visibility setting
     * @param originalSendReport The original send_report setting from domain/selection
     * @returns Calculated send_report value
     */
    calculateSendReport (visible: boolean, originalSendReport: boolean): boolean {
        return visible && originalSendReport;
    }
}
